/**
 * EmbarkAI iframe Wallet - Main Entry Point
 *
 * Secure isolated context for MPC key share storage and cryptographic operations
 * Runs at auth.embarkai.io/ with origin isolation from parent domains
 */

import { sendAccountRequest } from './lib/account-requests'
import { ActivityLoggingAPI } from './lib/activity-logging-api'
import { AuthorizationManager } from './lib/authorization-manager'
import { BackupManager } from './lib/backup-manager'
import { restoreErrorForParent } from './lib/backup/server-backup'
import { DKGManager } from './lib/dkg-manager'
import { HyperlaneAPI } from './lib/hyperlane'
import { iframeSession } from './lib/iframe-session'
import { IframeSignIn } from './lib/iframe-sign-in'
import { BinanceOnrampAPI } from './lib/onramp/binance'
// import { FinchPayRampAPI } from './lib/onramp/finchpay'
// import { RampnowOnrampAPI } from './lib/onramp/rampnow'
import { OptedOutAppsManager } from './lib/opted-out-apps-manager'
import { SdkChannelManager } from './lib/sdk-channel-manager'
import { SecureMessenger } from './lib/secure-messenger'
import {
  applyMessageAuth,
  basicBackupAllowed,
  CONSENT_MESSAGE,
  resolveMessageAuth,
  SESSION_FREE_MESSAGES,
  type MessageAuth
} from './lib/session-auth'
import { SigningManager } from './lib/signing-manager'
import { StorageManager } from './lib/storage-manager'
import { TrustedAppsManager } from './lib/trusted-apps-manager'
import { UserPolicyAPI } from './lib/user-policy-api'
import type { EnsureServerBackupResult, SecureMessage } from './lib/types'
import { buildReadyIndicatorData, TemplateEngine, TemplateLoader } from './templates'

const IFRAME_VERSION = __EMBARK_SDK_VERSION__

/** Owner address of the wallet the parent names in a backup request (sdk #32); older parents send none */
function walletOwnerOf(data: { walletOwner?: unknown } | undefined): string | undefined {
  const owner = data?.walletOwner
  return typeof owner === 'string' && /^0x[0-9a-fA-F]{40}$/.test(owner) ? owner : undefined
}

/** What the parent may learn about a restored backup: never the keyshare */
function publicBackupInfo(data: { userId: string; sessionId: string; ownerAddress: string }) {
  return { userId: data.userId, sessionId: data.sessionId, ownerAddress: data.ownerAddress }
}

class IframeWallet {
  private messenger: SecureMessenger
  private sdkChannelManager: SdkChannelManager
  private dkgManager: DKGManager
  private signingManager: SigningManager
  private authManager: AuthorizationManager
  private storage: StorageManager
  private trustedApps: TrustedAppsManager
  private optedOutApps: OptedOutAppsManager
  private backupManager: BackupManager
  private binance: BinanceOnrampAPI
  private hyperlane: HyperlaneAPI
  private userPolicies: UserPolicyAPI
  private activityLogging: ActivityLoggingAPI
  private signIn: IframeSignIn
  // private finchpay: FinchPayRampAPI
  // private rampnow: RampnowOnrampAPI

  constructor() {
    console.log('='.repeat(60))
    console.log(`  EmbarkAI Secure Wallet - iframe version ${IFRAME_VERSION}`)
    console.log('='.repeat(60))

    this.messenger = new SecureMessenger()
    this.sdkChannelManager = new SdkChannelManager()
    this.dkgManager = new DKGManager()
    this.signingManager = new SigningManager()
    this.authManager = new AuthorizationManager()
    this.storage = new StorageManager()
    this.trustedApps = new TrustedAppsManager()
    this.optedOutApps = new OptedOutAppsManager()
    this.backupManager = new BackupManager()
    this.signIn = new IframeSignIn()

    this.userPolicies = new UserPolicyAPI(this.messenger.sendResponse.bind(this.messenger))
    this.activityLogging = new ActivityLoggingAPI(this.messenger.sendResponse.bind(this.messenger))

    this.hyperlane = new HyperlaneAPI(this.messenger.sendResponse.bind(this.messenger))

    this.binance = new BinanceOnrampAPI(
      this.messenger.sendResponse.bind(this.messenger),
      (type: string, data: any, targetOrigin: string) => {
        window.parent.postMessage(
          {
            type,
            data,
            timestamp: Date.now()
          },
          targetOrigin
        )
      }
    )

    // this.rampnow = new RampnowOnrampAPI(this.messenger.sendResponse.bind(this.messenger))

    // this.finchpay = new FinchPayRampAPI(
    //   this.messenger.sendResponse.bind(this.messenger),
    //   (type: string, data: any, targetOrigin: string) => {
    //     window.parent.postMessage(
    //       {
    //         type,
    //         data,
    //         timestamp: Date.now()
    //       },
    //       targetOrigin
    //     )
    //   }
    // )
  }

  async initialize(): Promise<void> {
    // Validate we're running in correct origin (for production)
    if (
      window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1' &&
      !window.location.hostname.includes('embarkai.com')
    ) {
      console.warn('[iframe] ⚠️ Running on unexpected origin!: ', window.location.hostname)
    }

    // Initialize WASM modules
    await Promise.all([this.dkgManager.initialize(), this.signingManager.initialize()])

    // Setup message listener
    this.messenger.setupListener(async (message, origin) => {
      await this.handleMessage(message, origin)
    })

    // Display ready indicator
    this.displayReadyIndicator()

    // Notify parent that iframe is ready
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type: 'IFRAME_MSG_IFRAME_READY',
          timestamp: Date.now()
        },
        '*' // Initial broadcast, will validate origin in subsequent messages
      )
    }
  }

  private async handleMessage(message: SecureMessage, origin: string): Promise<void> {
    const { type, messageId } = message

    try {
      // Which user and token the message acts with: with an iframe session the parent's accessToken and
      // userId are ignored (tss-api #6). Sign-in and session messages handle the session themselves.
      let auth: MessageAuth = { mode: 'legacy' }
      if (!SESSION_FREE_MESSAGES.has(type)) {
        const channel = this.sdkChannelManager.getChannel(message.data?.sessionToken)
        const channelSupportsIframeSession = !!channel && channel.origin === origin && !!channel.sdkVersion
        auth = await resolveMessageAuth(message.data, channelSupportsIframeSession, iframeSession, {
          includeUnconfirmed: type === CONSENT_MESSAGE
        })
        message.data = applyMessageAuth(message.data, auth)
      }

      switch (true) {
        case type.startsWith('USER_POLICY_'):
          await this.userPolicies.handleMessage(message, origin)
          break

        case type.startsWith('USER_ACTIVITY_LOG_'):
          await this.activityLogging.handleMessage(message, origin)
          break

        case type.startsWith('HYPERLANE_'):
          await this.hyperlane.handleMessage(message, origin)
          break

        // case type.startsWith('FINCHPAY_'):
        //   await this.finchpay.handleMessage(message, origin)
        //   break

        case type.startsWith('BINANCE_'):
          await this.binance.handleMessage(message, origin)
          break

        // case type.startsWith('RAMPNOW_'):
        //   await this.rampnow.handleMessage(message, origin)
        //   break

        case type === 'SDK_AUTH':
          await this.handleSDKAuth(message, origin)
          break

        case type === 'SDK_CHANNEL_HEARTBEAT':
          await this.handleHeartbeat(message, origin)
          break

        case type === 'SIGN_IN_START':
          await this.handleSignInStart(message, origin)
          break

        case type === 'SIGN_IN_FINISH':
          await this.handleSignInFinish(message, origin)
          break

        case type === 'SIGN_OUT':
          await this.handleSignOut(message, origin)
          break

        case type === 'GET_SESSION':
          await this.handleGetSession(message, origin)
          break

        case type === 'GET_DAPP_TOKENS':
          await this.handleGetDappTokens(message, origin)
          break

        case type === 'ACCOUNT_REQUEST':
          await this.handleAccountRequest(message, origin)
          break

        case type === 'AUTHENTICATE':
          await this.handleAuthenticate(message, origin)
          break

        case type === 'START_DKG':
          await this.handleStartDKG(message, origin, auth)
          break

        case type === 'SIGN_TRANSACTION':
          await this.handleSignTransaction(message, origin)
          break

        case type === 'SIGN_TYPED_DATA':
          await this.handleSignTypedData(message, origin)
          break

        case type === 'GET_ADDRESS':
          await this.handleGetAddress(message, origin)
          break

        case type === 'CHECK_KEYSHARE':
          await this.handleCheckKeyshare(message, origin)
          break

        case type === 'GET_TRUSTED_APPS':
          await this.handleGetTrustedApps(message, origin)
          break

        case type === 'REMOVE_TRUSTED_APP':
          await this.handleRemoveTrustedApp(message, origin)
          break

        case type === 'CLEAR_AUTHORIZATIONS':
          await this.handleClearAuthorizations(message, origin)
          break

        case type === 'CREATE_BACKUP':
          await this.handleCreateBackup(message, origin, auth)
          break

        case type === 'GET_BACKUP_STATUS':
          await this.handleGetBackupStatus(message, origin)
          break

        case type === 'ENSURE_SERVER_BACKUP':
          await this.handleEnsureServerBackup(message, origin, auth)
          break

        case type === 'GET_CLOUD_PROVIDERS':
          await this.handleGetCloudProviders(message, origin)
          break

        case type === 'RESTORE_BACKUP':
          await this.handleRestoreBackup(message, origin, auth)
          break

        case type === 'RESTORE_FROM_FILE':
          await this.handleRestoreFromFile(message, origin)
          break

        case type === 'ENCRYPT_BACKUP_DATA':
          await this.handleEncryptBackupData(message, origin)
          break

        case type === 'ENCRYPT_ENVELOPE_DATA':
          await this.handleEncryptEnvelopeData(message, origin)
          break

        case type === 'DECRYPT_AND_RESTORE_ENVELOPE':
          await this.handleDecryptAndRestoreEnvelope(message, origin)
          break

        default:
          throw new Error(`Unknown message type: ${type}`)
      }
    } catch (error) {
      console.error(`[iframe] ❌ Error handling ${type}:`, error)
      this.messenger.sendError(messageId, error instanceof Error ? error : new Error('Unknown error'), origin)
    }
  }

  /**
   * Handle SDK channel heartbeat - allows parent to check if channel is still valid
   */
  private async handleHeartbeat(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken } = message.data // sessionToken for backward compatibility
    const { messageId } = message

    const isValid = this.sdkChannelManager.validateChannel(sessionToken, origin)
    const stats = this.sdkChannelManager.getStats()

    this.messenger.sendResponse(
      messageId,
      {
        type: 'IFRAME_MSG_HEARTBEAT_RESPONSE',
        valid: isValid,
        channelsCount: stats.total
      },
      origin
    )

    console.log(`[iframe] HEARTBEAT: valid=${isValid}, channels=${stats.total}`)
  }

  private async handleSDKAuth(message: SecureMessage, origin: string): Promise<void> {
    const { projectId, sdkVersion } = message.data
    const { messageId } = message

    console.log(`[iframe] SDK_AUTH: projectId=${projectId}, sdkVersion=${sdkVersion ?? 'legacy'}`)

    // Validate HTTPS origin
    const isLocalhost = origin.startsWith('http://localhost:') || origin.startsWith('http://127.0.0.1:')
    const isHttps = origin.startsWith('https://')
    if (!isHttps && !isLocalhost) {
      throw new Error('Only HTTPS origins are allowed (or localhost for dev)')
    }

    // TODO: Validate project ID belongs to origin (backend API call)
    // For now, accept all HTTPS origins

    // Create SDK channel
    // An SDK that announces its version supports iframe sessions; older ones keep the legacy mode (D10, D11)
    const sessionToken = this.sdkChannelManager.createChannel(
      projectId,
      origin,
      typeof sdkVersion === 'string' && sdkVersion ? sdkVersion.slice(0, 32) : undefined
    )

    // Configure messenger with SDK channel token
    this.messenger.setChannelToken(sessionToken)
    this.messenger.addAllowedOrigin(origin)

    // Send response
    this.messenger.sendResponse(
      messageId,
      {
        type: 'IFRAME_MSG_SDK_AUTH_SUCCESS',
        sessionToken
      },
      origin
    )

    console.log(`[iframe] ✅ SDK authenticated for origin: ${origin.substring(0, 40)}...`)
  }

  private requireChannel(message: SecureMessage, origin: string) {
    const channel = this.sdkChannelManager.validateChannel(message.data?.sessionToken, origin)
      ? this.sdkChannelManager.getChannel(message.data.sessionToken)
      : null
    if (!channel) throw new Error('Invalid SDK channel')
    return channel
  }

  /** Sign-in requests: the parent passes the user input, the iframe holds the flow secret */
  private async handleSignInStart(message: SecureMessage, origin: string): Promise<void> {
    const channel = this.requireChannel(message, origin)
    const result = await this.signIn.start(message.data as any, origin, channel.projectId)
    this.messenger.sendResponse(message.messageId, { type: 'IFRAME_MSG_SIGN_IN_STARTED', ...result }, origin)
  }

  /** Finishes the flow: the iframe keeps the session, the parent gets a dApp token pair */
  private async handleSignInFinish(message: SecureMessage, origin: string): Promise<void> {
    const channel = this.requireChannel(message, origin)
    const loginResponse = await this.signIn.finish(message.data as any, origin, channel.projectId)
    this.messenger.sendResponse(message.messageId, { type: 'IFRAME_MSG_SIGN_IN_FINISHED', loginResponse }, origin)
  }

  private async handleSignOut(message: SecureMessage, origin: string): Promise<void> {
    this.requireChannel(message, origin)
    this.signIn.cancel(origin)
    await iframeSession.signOut()
    this.messenger.sendResponse(message.messageId, { type: 'IFRAME_MSG_SIGNED_OUT', success: true }, origin)
  }

  /** Session state and profile basics, never the tokens */
  private async handleGetSession(message: SecureMessage, origin: string): Promise<void> {
    this.requireChannel(message, origin)
    this.messenger.sendResponse(message.messageId, { type: 'IFRAME_MSG_SESSION', session: iframeSession.info() }, origin)
  }

  /** A new dApp pair for the parent (lost storage, dead refresh token); null when the iframe has no session */
  private async handleGetDappTokens(message: SecureMessage, origin: string): Promise<void> {
    const channel = this.requireChannel(message, origin)
    const info = iframeSession.info()
    const tokens = info.signedIn
      ? {
          ...(await iframeSession.mintDappTokens(channel.projectId)),
          hasKeyshare: !!info.hasKeyshare,
          displayName: info.displayName ?? null,
          avatar: info.avatar ?? null,
          providers: info.providers ?? []
        }
      : null
    this.messenger.sendResponse(message.messageId, { type: 'IFRAME_MSG_DAPP_TOKENS', tokens }, origin)
  }

  /** Account changes a dApp token cannot make (profile, nickname, devices, smart account), sent with the iframe session */
  private async handleAccountRequest(message: SecureMessage, origin: string): Promise<void> {
    const channel = this.requireChannel(message, origin)
    const result = await sendAccountRequest(message.data as any, channel.projectId, undefined, undefined, origin)
    this.messenger.sendResponse(message.messageId, { type: 'IFRAME_MSG_ACCOUNT_RESPONSE', ...result }, origin)
  }

  private async handleAuthenticate(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken, projectId, userId, avatar, displayName, accessToken } = message.data
    const { messageId } = message

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      throw new Error('Invalid SDK channel')
    }

    console.log(`[iframe] AUTHENTICATE: userId=${userId}, projectId=${projectId}`)

    // Check authorization
    const isAuthorized = await this.authManager.checkAuthorization(userId, projectId)

    if (!isAuthorized) {
      // Check if project is a trusted authorized app (skip auth dialog)
      const isTrustedApp = await this.authManager.isTrustedAuthorizedApp(projectId)

      if (isTrustedApp) {
        console.log(`[iframe] Project ${projectId} is a trusted authorized app, auto-authorizing`)
        await this.authManager.storeAuthorization(userId, projectId, origin)
        console.log(`[iframe] ✅ Auto-authorization granted for trusted app: ${userId} -> ${projectId}`)

        // Auto-add to user's trusted apps list (for transaction consent bypass)
        // Only if user hasn't opted-out of auto-trust for this app
        try {
          const isOptedOut = this.optedOutApps.isOptedOut(userId, projectId, origin)
          console.log(`[iframe] Checking opt-out status: ${isOptedOut}`)

          if (!isOptedOut) {
            const metadata = await this.authManager.getProjectMetadata(projectId)
            console.log(`[iframe] Got metadata for auto-trust:`, metadata?.name, metadata?.logo)
            this.trustedApps.addTrustedApp(userId, projectId, origin, metadata?.name, metadata?.logo)
            console.log(`[iframe] ✅ Auto-added to trusted apps list for seamless transactions`)
          } else {
            console.log(`[iframe] ℹ️ User opted-out of auto-trust for this app, skipping`)
          }
        } catch (error) {
          console.error(`[iframe] ❌ Error adding to trusted apps list:`, error)
        }
      } else {
        console.log(`[iframe] Authorization needed for project: ${projectId}`)

        // Get project info (TODO: from backend API)
        const projectInfo = {
          id: projectId,
          name: 'Application',
          logoUrl: '',
          website: origin,
          domains: [origin]
        }

        // Request user authorization with user profile data
        const consent = await this.authManager.requestAuthorization(projectInfo, origin, { avatar, displayName })

        if (!consent) {
          throw new Error('User denied authorization')
        }

        // Store authorization
        await this.authManager.storeAuthorization(userId, projectId, origin)
        console.log(`[iframe] ✅ Authorization granted: ${userId} -> ${projectId}`)
      }
    }

    // The user passed the consent step (or had passed it before) for this user and project: a session from the
    // sign-in becomes usable. Only a session of that same user is confirmed; this holds in legacy mode too (a page
    // that still stores an older session's tokens while the user signs in again through the iframe).
    iframeSession.confirm(userId)

    // Make the keyshare of this project's wallet current before reporting it: another tab may have made a
    // sibling's current (#31). Only when the message carries a token; otherwise CHECK_KEYSHARE reconciles later.
    if (accessToken) {
      const walletOwner = await this.backupManager.walletOwnerForProject(userId, accessToken, projectId)
      if (walletOwner) this.storage.reconcileWithServer(userId, { hasKeyshare: true, ownerAddress: walletOwner })
    }

    // Get user address
    const address = this.storage.getOwnerAddress(userId)

    // Send response
    this.messenger.sendResponse(
      messageId,
      {
        type: 'IFRAME_MSG_AUTH_SUCCESS',
        userId,
        address
      },
      origin
    )

    console.log(`[iframe] ✅ User authenticated: ${userId}`)

    // Check if user already has keyshare (e.g., after page refresh)
    // If yes, wallet is ready - no need for DKG or restore
    if (this.storage.hasKeyshare(userId)) {
      console.log(`[iframe] ℹ️ User has existing keyshare, sending WALLET_READY`)
      this.sendWalletReadyStatus(userId, origin)
    } else {
      console.log(`[iframe] ℹ️ User needs DKG or restore to create/recover keyshare`)
    }
  }

  private async handleStartDKG(message: SecureMessage, origin: string, auth: MessageAuth): Promise<void> {
    const { sessionToken, userId, projectId, accessToken } = message.data
    const { messageId } = message

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      throw new Error('Invalid SDK channel')
    }

    // Check authorization
    const isAuthorized = await this.authManager.checkAuthorization(userId, projectId)
    if (!isAuthorized) {
      throw new Error('User has not authorized this application')
    }

    console.log(`[iframe] START_DKG: userId=${userId}, projectId=${projectId}, hasToken=${!!accessToken}`)

    // Show loading
    this.displayLoadingIndicator('Generating secure keys...')

    try {
      let ownerAddress: `0x${string}`
      try {
        // Perform DKG with projectId and accessToken
        const result = await this.dkgManager.performDKG(userId, projectId, accessToken)

        // Whatever asked for this DKG (an older SDK, a failed status check), the keyshare it
        // replaces may still be the right one for another project: set it aside, never overwrite it
        const previous = this.storage.getOwnerAddress(userId)
        if (previous && previous.toLowerCase() !== result.ownerAddress.toLowerCase()) {
          this.storage.archiveKeyshare(userId)
        }

        // Save to IFRAME localStorage (ISOLATED)
        this.storage.saveKeyshare(userId, {
          keyshare_b64: result.keyshare.keyshare_b64,
          ownerAddress: result.ownerAddress,
          sessionId: result.sessionId
        })
        ownerAddress = result.ownerAddress
      } catch (error) {
        // The server already holds a keyshare for this project: if its other half is the current
        // keyshare or was set aside here, use it instead of failing
        const existing = existingOwnerAddressOf(error)
        const current = this.storage.getOwnerAddress(userId)
        const isCurrent = !!existing && !!current && current.toLowerCase() === existing.toLowerCase()
        if (!existing || (!isCurrent && !this.storage.restoreArchivedKeyshare(userId, existing))) throw error
        ownerAddress = this.storage.getOwnerAddress(userId) as `0x${string}`
      }
      const result = { ownerAddress }

      // Hide loading
      this.hideLoadingIndicator()

      // Send response
      this.messenger.sendResponse(
        messageId,
        {
          type: 'IFRAME_MSG_DKG_SUCCESS',
          ownerAddress: result.ownerAddress
        },
        origin
      )

      console.log(`[iframe] ✅ DKG completed: ${result.ownerAddress}`)

      // Basic backup right away, so a new wallet is recoverable before the user does anything.
      // Not awaited: the wallet must not wait for it, the parent joins the same run via ENSURE_SERVER_BACKUP.
      void this.backupManager.ensureServerBackup(userId, accessToken, projectId, basicBackupAllowed(auth), ownerAddress)
      if (auth.mode === 'iframe') iframeSession.update({ hasKeyshare: true })

      // Send wallet ready status after DKG
      this.sendWalletReadyStatus(userId, origin)
    } catch (error) {
      this.hideLoadingIndicator()
      throw error
    }
  }

  private async handleSignTransaction(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken, userId, projectId, transaction, accessToken, avatar, displayName } = message.data
    const { messageId } = message

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      throw new Error('Invalid SDK channel')
    }

    // Check authorization
    const isAuthorized = await this.authManager.checkAuthorization(userId, projectId)
    if (!isAuthorized) {
      throw new Error('User has not authorized this application')
    }

    console.log(`[iframe] SIGN_TRANSACTION: userId=${userId}, hasAccessToken=${!!accessToken}`)

    try {
      // Sign transaction (includes user confirmation)
      // the keyshare of this project's wallet, not whichever one another tab made current (#31)
      const walletOwner = await this.backupManager.walletOwnerForProject(userId, accessToken, projectId)
      const signature = await this.signingManager.signTransaction(
        userId,
        projectId,
        origin,
        transaction,
        accessToken,
        { avatar, displayName },
        walletOwner
      )

      // Send response
      this.messenger.sendResponse(
        messageId,
        {
          type: 'IFRAME_MSG_SIGNATURE',
          signature
        },
        origin
      )

      console.log(`[iframe] ✅ Transaction signed`)
    } catch (error) {
      console.error('[iframe] Signing failed:', error)
      throw error
    }
  }

  private async handleSignTypedData(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken, userId, projectId, typedData, digest32, accessToken, avatar, displayName } = message.data
    const { messageId } = message

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      throw new Error('Invalid SDK channel')
    }

    // Check authorization
    const isAuthorized = await this.authManager.checkAuthorization(userId, projectId)
    if (!isAuthorized) {
      throw new Error('User has not authorized this application')
    }

    console.log(`[iframe] SIGN_TYPED_DATA: userId=${userId}, primaryType=${typedData?.primaryType}`)

    try {
      // Sign typed data (includes user confirmation), with the keyshare of this project's wallet (#31)
      const walletOwner = await this.backupManager.walletOwnerForProject(userId, accessToken, projectId)
      const signature = await this.signingManager.signTypedData(
        userId,
        projectId,
        origin,
        typedData,
        digest32,
        accessToken,
        { avatar, displayName },
        walletOwner
      )

      // Send response
      this.messenger.sendResponse(
        messageId,
        {
          type: 'IFRAME_MSG_EIP712_SIGNATURE',
          signature
        },
        origin
      )

      console.log(`[iframe] ✅ EIP712 message signed`)
    } catch (error) {
      console.error('[iframe] EIP712 signing failed:', error)
      throw error
    }
  }

  private async handleGetAddress(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken, userId } = message.data
    const { messageId } = message

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      throw new Error('Invalid SDK channel')
    }

    const address = this.storage.getOwnerAddress(userId)

    this.messenger.sendResponse(
      messageId,
      {
        type: 'IFRAME_MSG_ADDRESS',
        address
      },
      origin
    )
  }

  private async handleCheckKeyshare(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken, userId, server } = message.data
    const { messageId } = message

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      throw new Error('Invalid SDK channel')
    }

    // The parent passes what the server holds for this project when it knows it; older parents do not
    if (server && typeof server.hasKeyshare === 'boolean') {
      this.storage.reconcileWithServer(userId, server)
    }

    const hasKeyshare = this.storage.hasKeyshare(userId)
    const address = hasKeyshare ? this.storage.getOwnerAddress(userId) : undefined

    this.messenger.sendResponse(
      messageId,
      {
        type: 'IFRAME_MSG_KEYSHARE_STATUS',
        hasKeyshare,
        address
      },
      origin
    )
  }

  private async handleGetTrustedApps(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken, userId } = message.data
    const { messageId } = message

    console.log(`[iframe] GET_TRUSTED_APPS: userId=${userId}, sessionToken=${sessionToken ? 'present' : 'missing'}`)

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      console.error('[iframe] GET_TRUSTED_APPS: Session validation failed')
      throw new Error('Invalid SDK channel')
    }

    const trustedApps = this.trustedApps.getTrustedAppsForUser(userId)

    this.messenger.sendResponse(
      messageId,
      {
        type: 'IFRAME_MSG_TRUSTED_APPS_LIST',
        apps: trustedApps
      },
      origin
    )

    console.log(`[iframe] ✅ GET_TRUSTED_APPS: Response sent with ${trustedApps.length} apps`)
  }

  private async handleRemoveTrustedApp(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken, userId, projectId, appOrigin } = message.data
    const { messageId } = message

    console.log(`[iframe] REMOVE_TRUSTED_APP: userId=${userId}, projectId=${projectId}, origin=${appOrigin}`)

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      console.error('[iframe] REMOVE_TRUSTED_APP: Session validation failed')
      throw new Error('Invalid SDK channel')
    }

    // Check if this is an ecosystem app (trustedAuthorizedApp: true)
    // If so, add to opted-out list to prevent auto-re-adding on next auth
    const isEcosystemApp = await this.authManager.isTrustedAuthorizedApp(projectId)
    if (isEcosystemApp) {
      try {
        this.optedOutApps.addOptedOut(userId, projectId, appOrigin)
        console.log(`[iframe] ℹ️ Ecosystem app removed - added to opted-out list`)
      } catch (e) {
        console.error('[iframe] Failed to persist opted-out flag; proceeding with removal:', e)
      }
    }

    // Remove trusted app
    this.trustedApps.removeTrustedApp(userId, projectId, appOrigin)

    this.messenger.sendResponse(
      messageId,
      {
        type: 'IFRAME_MSG_TRUSTED_APP_REMOVED',
        success: true
      },
      origin
    )

    console.log(`[iframe] ✅ REMOVE_TRUSTED_APP: App removed`)
  }

  private async handleClearAuthorizations(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken, projectId } = message.data
    const { messageId } = message

    console.log(`[iframe] CLEAR_AUTHORIZATIONS: projectId=${projectId}`)

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      console.error('[iframe] CLEAR_AUTHORIZATIONS: Session validation failed')
      throw new Error('Invalid SDK channel')
    }

    // Clear all authorizations for this project
    // This will iterate through all stored authorizations and remove those matching the projectId
    const storage = this.storage as any
    const allKeys = Object.keys(localStorage).filter((key) => key.startsWith('auth_'))

    let clearedCount = 0
    allKeys.forEach((key) => {
      try {
        const authData = JSON.parse(localStorage.getItem(key) || '{}')
        if (authData.projectId === projectId) {
          localStorage.removeItem(key)
          clearedCount++
        }
      } catch (e) {
        // Skip invalid entries
      }
    })

    this.messenger.sendResponse(
      messageId,
      {
        type: 'IFRAME_MSG_AUTHORIZATIONS_CLEARED',
        success: true,
        clearedCount
      },
      origin
    )

    console.log(`[iframe] ✅ CLEAR_AUTHORIZATIONS: Cleared ${clearedCount} authorizations for project ${projectId}`)
  }

  private async handleCreateBackup(message: SecureMessage, origin: string, auth: MessageAuth): Promise<void> {
    const { sessionToken, userId, backupRequest, accessToken } = message.data
    const { messageId, projectId } = message

    console.log(`[iframe] CREATE_BACKUP received:`, {
      userId,
      method: backupRequest?.method,
      hasSessionToken: !!sessionToken,
      hasAccessToken: !!accessToken,
      origin
    })

    if (!userId) {
      console.error('[iframe] CREATE_BACKUP: userId is missing!')
      this.messenger.sendError(messageId, 'userId is required', origin)
      return
    }

    if (!backupRequest) {
      console.error('[iframe] CREATE_BACKUP: backupRequest is missing!')
      this.messenger.sendError(messageId, 'backupRequest is required', origin)
      return
    }

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      console.error('[iframe] CREATE_BACKUP: Session validation failed', { sessionToken, origin })
      this.messenger.sendError(messageId, 'Invalid SDK channel', origin)
      return
    }

    try {
      // Create backup using BackupManager
      // Network lookups happen before the passkey confirmation, so the passkey prompt follows the click
      // The backup of the wallet of the request's project, not of whatever keyshare another tab made current
      const walletOwner = await this.resolveWalletOwner(userId, message)
      const currentServerMethod =
        backupRequest.method === 'server' ? await this.backupManager.getServerBackupMethod(accessToken, projectId, walletOwner) : undefined
      const backupOp = () =>
        this.backupManager.createBackup(userId, backupRequest, accessToken, projectId, currentServerMethod, basicBackupAllowed(auth), walletOwner)
      const result =
        !backupRequest.password && this.isSafari()
          ? await this.withPasskeyConfirmation('backup', backupOp)
          : await backupOp()

      this.messenger.sendResponse(
        messageId,
        {
          type: 'IFRAME_MSG_BACKUP_CREATED',
          result
        },
        origin
      )

      if (result.success) {
        console.log(`[iframe] ✅ CREATE_BACKUP: Backup created successfully`)
      } else {
        console.error(`[iframe] ❌ CREATE_BACKUP: Backup failed - ${result.error}`)
      }
    } catch (error: any) {
      console.error('[iframe] CREATE_BACKUP: Exception:', error)
      this.messenger.sendError(messageId, error.message || 'Backup failed', origin)
    }
  }

  /**
   * The wallet a backup request is about (sdk #32, #31): the server's keyshare for the request's project as TSS
   * reports it, also for older parents that name none; what the parent names only when TSS does not answer
   */
  private resolveWalletOwner(userId: string, message: SecureMessage): Promise<string | undefined> {
    return this.backupManager.resolveWalletOwner(userId, walletOwnerOf(message.data), message.data?.accessToken, message.projectId)
  }

  private async handleGetBackupStatus(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken, userId } = message.data
    const { messageId } = message

    console.log(`[iframe] GET_BACKUP_STATUS: userId=${userId}`)

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      console.error('[iframe] GET_BACKUP_STATUS: Session validation failed')
      throw new Error('Invalid SDK channel')
    }

    // Get backup status
    const status = this.backupManager.getBackupStatus(userId)

    this.messenger.sendResponse(
      messageId,
      {
        type: 'IFRAME_MSG_BACKUP_STATUS',
        status
      },
      origin
    )

    console.log(`[iframe] ✅ GET_BACKUP_STATUS: Status retrieved`)
  }

  private async handleEnsureServerBackup(message: SecureMessage, origin: string, auth: MessageAuth): Promise<void> {
    const { sessionToken, userId, accessToken } = message.data
    const { messageId, projectId } = message

    console.log(`[iframe] ENSURE_SERVER_BACKUP: userId=${userId}`)

    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      console.error('[iframe] ENSURE_SERVER_BACKUP: Session validation failed')
      throw new Error('Invalid SDK channel')
    }

    const result: EnsureServerBackupResult = userId
      ? await this.backupManager.ensureServerBackup(userId, accessToken, projectId, basicBackupAllowed(auth), await this.resolveWalletOwner(userId, message))
      : { status: 'failed', reason: 'unknown', error: 'userId is required' }

    this.messenger.sendResponse(messageId, { type: 'IFRAME_MSG_SERVER_BACKUP_ENSURED', result }, origin)
  }

  private async handleGetCloudProviders(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken } = message.data
    const { messageId } = message

    console.log(`[iframe] GET_CLOUD_PROVIDERS`)

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      console.error('[iframe] GET_CLOUD_PROVIDERS: Session validation failed')
      throw new Error('Invalid SDK channel')
    }

    // Get available cloud providers
    const providers = this.backupManager.getAvailableCloudProviders()

    this.messenger.sendResponse(
      messageId,
      {
        type: 'IFRAME_MSG_CLOUD_PROVIDERS',
        providers
      },
      origin
    )

    console.log(`[iframe] ✅ GET_CLOUD_PROVIDERS: Providers retrieved`)
  }

  /**
   * Safari/WebKit requires focused document + user gesture for WebAuthn in cross-origin iframe.
   * Shows a modal with a button so the user click provides both.
   */
  /**
   * Safari/WebKit requires focused document + user gesture for WebAuthn in cross-origin iframe.
   * Shows a confirmation modal, waits for click, then runs the passkey operation.
   * Hides iframe after operation completes (success or failure).
   */
  private async withPasskeyConfirmation<T>(action: 'restore' | 'backup', operation: () => Promise<T>): Promise<T> {
    this.showIframe()

    const title = action === 'restore' ? 'Restore Backup' : 'Create Backup'
    const description =
      action === 'restore'
        ? 'Authenticate with your passkey to restore your wallet backup.'
        : 'Authenticate with your passkey to encrypt your wallet backup.'

    const template = TemplateLoader.getTemplate('passkey-confirm')
    const html = TemplateEngine.render(template, { title, description })

    const overlay = document.createElement('div')
    overlay.innerHTML = html
    document.body.appendChild(overlay)

    const confirmed = await new Promise<boolean>((resolve) => {
      overlay.querySelector('.confirm-btn')!.addEventListener('click', (e) => {
        if (e.isTrusted) resolve(true)
      })
      overlay.querySelector('.cancel-btn')!.addEventListener('click', () => resolve(false))
    })

    if (!confirmed) {
      overlay.remove()
      this.hideIframe()
      throw new Error('User cancelled passkey authentication')
    }

    // Replace buttons with loading — overlay stays visible over ready indicator
    const actions = overlay.querySelector('.passkey-actions') as HTMLElement
    const status = overlay.querySelector('.passkey-status') as HTMLElement
    actions.innerHTML = '<div class="spinner" style="margin:0 auto"></div>'
    status.textContent = 'Waiting for passkey...'

    try {
      return await operation()
    } finally {
      overlay.remove()
      this.hideIframe()
    }
  }

  private showIframe(): void {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'IFRAME_MSG_SHOW_IFRAME', timestamp: Date.now() }, '*')
    }
  }

  private hideIframe(): void {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'IFRAME_MSG_HIDE_IFRAME', timestamp: Date.now() }, '*')
    }
  }

  private isSafari(): boolean {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  }

  private async handleRestoreBackup(message: SecureMessage, origin: string, auth: MessageAuth): Promise<void> {
    const { sessionToken, userId, password, accessToken } = message.data
    const { messageId, projectId } = message

    console.log(`[iframe] RESTORE_BACKUP received:`, {
      userId,
      hasPassword: !!password,
      hasSessionToken: !!sessionToken,
      hasAccessToken: !!accessToken,
      origin
    })

    if (!userId) {
      console.error('[iframe] RESTORE_BACKUP: userId is missing!')
      this.messenger.sendError(messageId, 'userId is required', origin)
      return
    }

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      console.error('[iframe] RESTORE_BACKUP: Session validation failed', { sessionToken, origin })
      this.messenger.sendError(messageId, 'Invalid SDK channel', origin)
      return
    }

    try {
      // Safari/WebKit requires focused document for WebAuthn credentials.get() in cross-origin iframe.
      // Show a confirmation modal so the user click gives focus + user gesture before passkey derivation.
      // Restore backup using BackupManager
      // The wallet the server holds for the request's project: a backup of another wallet of the user is not restored
      const walletOwner = await this.resolveWalletOwner(userId, message)
      const restoreOp = () =>
        this.backupManager.restoreFromServer(userId, password, accessToken, projectId, basicBackupAllowed(auth), walletOwner)
      // Basic backups need no passkey, so no confirmation either
      const needsPasskey =
        !password &&
        this.isSafari() &&
        (await this.backupManager.getServerBackupMethod(accessToken, projectId, walletOwner)) !== 'server-managed'
      const backupData = needsPasskey ? await this.withPasskeyConfirmation('restore', restoreOp) : await restoreOp()

      this.messenger.sendResponse(
        messageId,
        {
          type: 'IFRAME_MSG_BACKUP_RESTORED',
          result: {
            success: true,
            timestamp: Date.now(),
            // the keyshare itself never leaves the iframe
            data: publicBackupInfo(backupData)
          }
        },
        origin
      )

      console.log(`[iframe] ✅ RESTORE_BACKUP: Backup restored successfully`)

      // Send wallet ready status after successful restore
      this.sendWalletReadyStatus(userId, origin)
    } catch (error: any) {
      console.error('[iframe] RESTORE_BACKUP: Exception:', error)
      // an older SDK can never open a Basic backup: tell its user where it opens, not to sign in again
      const parentSupportsIframeSession = !!this.sdkChannelManager.getChannel(sessionToken)?.sdkVersion

      this.messenger.sendResponse(
        messageId,
        {
          type: 'IFRAME_MSG_BACKUP_RESTORED',
          result: {
            success: false,
            timestamp: Date.now(),
            error: restoreErrorForParent(error.message || 'Restore failed', parentSupportsIframeSession)
          }
        },
        origin
      )

      console.log(`[iframe] ❌ RESTORE_BACKUP: Restore failed - ${error.message}`)
    }
  }

  private async handleRestoreFromFile(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken, userId, fileContent, password } = message.data
    const { messageId } = message

    console.log(`[iframe] RESTORE_FROM_FILE received:`, {
      userId,
      hasPassword: !!password,
      hasFileContent: !!fileContent,
      hasSessionToken: !!sessionToken,
      origin
    })

    if (!userId) {
      console.error('[iframe] RESTORE_FROM_FILE: userId is missing!')
      this.messenger.sendError(messageId, 'userId is required', origin)
      return
    }

    if (!fileContent) {
      console.error('[iframe] RESTORE_FROM_FILE: fileContent is missing!')
      this.messenger.sendError(messageId, 'fileContent is required', origin)
      return
    }

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      console.error('[iframe] RESTORE_FROM_FILE: Session validation failed', { sessionToken, origin })
      this.messenger.sendError(messageId, 'Invalid SDK channel', origin)
      return
    }

    try {
      // Restore from local file using BackupManager
      const backupData = await this.backupManager.restoreFromLocalFile(fileContent, userId, password)

      this.messenger.sendResponse(
        messageId,
        {
          type: 'IFRAME_MSG_FILE_RESTORED',
          result: {
            success: true,
            timestamp: Date.now(),
            // the keyshare itself never leaves the iframe
            data: publicBackupInfo(backupData)
          }
        },
        origin
      )

      console.log(`[iframe] ✅ RESTORE_FROM_FILE: File restored successfully`)

      // Send wallet ready status after successful file restore
      this.sendWalletReadyStatus(userId, origin)
    } catch (error: any) {
      console.error('[iframe] RESTORE_FROM_FILE: Exception:', error)

      this.messenger.sendResponse(
        messageId,
        {
          type: 'IFRAME_MSG_FILE_RESTORED',
          result: {
            success: false,
            timestamp: Date.now(),
            error: error.message || 'File restore failed'
          }
        },
        origin
      )

      console.log(`[iframe] ❌ RESTORE_FROM_FILE: Restore failed - ${error.message}`)
    }
  }

  private async handleEncryptBackupData(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken, userId, password } = message.data
    const { messageId } = message

    console.log(`[iframe] ENCRYPT_BACKUP_DATA received:`, {
      userId,
      hasPassword: !!password,
      hasSessionToken: !!sessionToken,
      origin
    })

    if (!userId) {
      console.error('[iframe] ENCRYPT_BACKUP_DATA: userId is missing!')
      this.messenger.sendError(messageId, 'userId is required', origin)
      return
    }

    // Validate session
    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      console.error('[iframe] ENCRYPT_BACKUP_DATA: Session validation failed', { sessionToken, origin })
      this.messenger.sendError(messageId, 'Invalid SDK channel', origin)
      return
    }

    try {
      // Encrypt backup data using BackupManager
      const encryptedData = await this.backupManager.encryptBackupData(userId, password, await this.resolveWalletOwner(userId, message))

      this.messenger.sendResponse(
        messageId,
        {
          type: 'IFRAME_MSG_BACKUP_ENCRYPTED',
          encryptedData
        },
        origin
      )

      console.log(`[iframe] ✅ ENCRYPT_BACKUP_DATA: Data encrypted successfully`)
    } catch (error: any) {
      console.error('[iframe] ENCRYPT_BACKUP_DATA: Exception:', error)
      this.messenger.sendError(messageId, error.message || 'Encryption failed', origin)
    }
  }

  private async handleEncryptEnvelopeData(message: SecureMessage, origin: string): Promise<void> {
    const { sessionToken, userId, password } = message.data
    const { messageId } = message

    if (!userId) {
      this.messenger.sendError(messageId, 'userId is required', origin)
      return
    }

    if (!this.sdkChannelManager.validateChannel(sessionToken, origin)) {
      this.messenger.sendError(messageId, 'Invalid SDK channel', origin)
      return
    }

    try {
      const envelopeJson = await this.backupManager.encryptEnvelopeData(userId, password, await this.resolveWalletOwner(userId, message))
      this.messenger.sendResponse(
        messageId,
        {
          type: 'IFRAME_MSG_ENVELOPE_ENCRYPTED',
          envelopeJson
        },
        origin
      )
    } catch (error: any) {
      this.messenger.sendError(messageId, error.message || 'Envelope encryption failed', origin)
    }
  }

  private async handleDecryptAndRestoreEnvelope(message: SecureMessage, origin: string): Promise<void> {
    const { userId, envelopeJson, password } = message.data
    const { messageId } = message

    if (!userId || !envelopeJson) {
      this.messenger.sendError(messageId, 'userId and envelopeJson are required', origin)
      return
    }

    try {
      await this.backupManager.decryptAndRestoreEnvelope(envelopeJson, password, userId)
      this.messenger.sendResponse(
        messageId,
        {
          type: 'IFRAME_MSG_ENVELOPE_RESTORED',
          result: { success: true, timestamp: Date.now() }
        },
        origin
      )
      this.sendWalletReadyStatus(userId, origin)
    } catch (error: any) {
      this.messenger.sendResponse(
        messageId,
        {
          type: 'IFRAME_MSG_ENVELOPE_RESTORED',
          result: { success: false, timestamp: Date.now(), error: error.message }
        },
        origin
      )
    }
  }

  private displayReadyIndicator(): void {
    const app = document.getElementById('app')
    if (!app) return

    // Build template data
    const templateData = buildReadyIndicatorData({
      origin: window.location.origin,
      iframeVersion: IFRAME_VERSION
    })

    // Load and render template
    const template = TemplateLoader.getTemplate('ready-indicator')
    const populatedReadyIndicatorHTML = TemplateEngine.render(template, templateData)

    app.innerHTML = populatedReadyIndicatorHTML
  }

  /**
   * Send wallet ready status to parent
   * This notifies the frontend that the wallet is fully initialized and ready to sign
   */
  private sendWalletReadyStatus(userId: string, origin: string): void {
    const hasKeyshare = this.storage.hasKeyshare(userId)
    const address = hasKeyshare ? this.storage.getOwnerAddress(userId) : undefined
    const hasChannel = this.sdkChannelManager.hasChannel(origin)

    const status = {
      ready: hasKeyshare && hasChannel,
      userId,
      address,
      hasKeyshare,
      hasChannel,
      hasSession: hasChannel, // backward compatibility
      timestamp: Date.now()
    }

    // Send as broadcast (not a response to specific message)
    if (window.parent) {
      window.parent.postMessage(
        {
          type: 'IFRAME_MSG_WALLET_READY',
          data: status,
          timestamp: Date.now()
        },
        origin
      )
    }
  }

  private displayLoadingIndicator(message: string): void {
    const app = document.getElementById('app')
    if (!app) return

    app.innerHTML = `
      <div class="loading-indicator">
        <div class="spinner"></div>
        <p>${message}</p>
      </div>
    `
  }

  private hideLoadingIndicator(): void {
    this.displayReadyIndicator()
  }
}

// Owner address of the keyshare the server refused to overwrite during DKG (409), if it named one
function existingOwnerAddressOf(error: unknown): string | null {
  const details = (error as { details?: { status?: unknown; existingOwnerAddress?: unknown } } | null)?.details
  if (details?.status !== 409 || typeof details.existingOwnerAddress !== 'string') return null
  return details.existingOwnerAddress
}

// Toggle auth permissions block at Authorization.html
function toggleAuthPermissions(e: MouseEvent) {
  const isToggler = (e.target as HTMLElement)?.id === 'data-auth-permissions-toggler'
  if (!isToggler) return

  const details = document.getElementById('data-auth-permissions-block')
  const isOpen = details.hasAttribute('aria-expanded') && details.getAttribute('aria-expanded') === 'true'

  const toggler = details.querySelector('#data-auth-permissions-toggler')

  if (isOpen) {
    details.setAttribute('aria-expanded', 'false')
    details.style.setProperty('--iframe-permissions-h', '0px')
    toggler.innerHTML = 'Show Permissions ▼'
  } else {
    details.setAttribute('aria-expanded', 'true')
    details.style.setProperty('--iframe-permissions-h', '148px')
    toggler.innerHTML = 'Hide Permissions ▲'
  }
}

// Toggle auth permissions block at Authorization.html
function toggleConfirmTransactionDetails(e: MouseEvent) {
  const isToggler = (e.target as HTMLElement)?.id === 'confirm-transaction-details-toggler'
  if (!isToggler) return

  const details = document.getElementById('confirm-transaction-details-block')
  const isOpen = details.hasAttribute('aria-expanded') && details.getAttribute('aria-expanded') === 'true'

  const toggler = details.querySelector('#confirm-transaction-details-toggler')

  if (isOpen) {
    details.setAttribute('aria-expanded', 'false')
    details.style.setProperty('--confirm-transaction-details-h', '0px')
    toggler.innerHTML = 'Show Transaction Details ▼'
  } else {
    details.setAttribute('aria-expanded', 'true')
    details.style.setProperty('--confirm-transaction-details-h', '200px')
    toggler.innerHTML = 'Hide Transaction Details ▲'
  }
}

// Initialize wallet when DOM is ready
function initWallet() {
  const wallet = new IframeWallet()
  wallet.initialize().catch((error) => {
    console.error('[iframe] ❌ Failed to initialize:', error)

    // Show error in UI
    const app = document.getElementById('app')
    if (app) {
      app.innerHTML = `
        <div class="error-indicator">
          <div class="error-icon">❌</div>
          <h2>Initialization Failed</h2>
          <p>${error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      `
    }
  })

  // Setup auth permissions toggler
  document.addEventListener('click', (e) => {
    toggleAuthPermissions(e)
    toggleConfirmTransactionDetails(e)
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWallet)
} else {
  initWallet()
}
